import { isDefined, throttle } from './utilities';

/**
 * Creates instances of all the classes applied to the fields
 *
 * @since 1.0
 */
export default class Fields {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		new DetectLoginSignupChanges();

		new MirrorPasswordField();
		new MirrorEmailField();
		new UsernameFieldVisibility();
		new RemovePlaceholders();
		new TabIndex();
		new EnableButtonBasedOnValidity();
	}
}

/**
 * Easy Digital Downloads doesn't trigger an event when the user changes
 * between logging in and signing up. This class listens to those changes
 * and triggers a custom event
 *
 * The event triggered is uo-edd-checkout/identification-source-change,
 * sends an object with "action", which is either "sign-up" or "log-in"
 */
class DetectLoginSignupChanges {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Listen changes to the payment method
		this.listenPaymentMethodLoad();
	}

	/**
	 * Listens to the payment method's AJAX call to load before listening
	 * to the changes in the container that has the sign-up / log-in fields
	 *
	 * @return {undefined}
	 */
	listenPaymentMethodLoad() {
		// Wait for the payment method to load
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Listen changes to the action
			this.listenActionChanges();
		} );
	}

	/**
	 * Listens if the action (sign up/log in) changed.
	 * EDD doesn't trigger a custom event, so we have to listen
	 * to the modifications made to an element.
	 *
	 * @return {undefined}
	 */
	listenActionChanges() {
		// Check if the required element exists
		if ( isDefined( this.$dynamicContainer ) ){
			// Observe changes to #edd_checkout_login_register, the element
			// that adds the sign up / log in fields dinamically
			const observer = new MutationObserver( () => {
				// Trigger a custom event
				this.triggerChangeEvent();
			} );

			// Start observing
			observer.observe( this.$dynamicContainer, {
				attributes: false,
				childList: true,
				subtree: false,
			} );
		}
	}

	/**
	 * Triggers a custom event including the current action
	 *
	 * @return {undefined}
	 */
	triggerChangeEvent() {
		// Create event
		const event = new CustomEvent( 'uo-edd-checkout/identification-source-change', {
			detail: {
				action: this.currentAction,
			},
		} );

		// Dispatch the event.
		document.body.dispatchEvent( event );
	}

	/**
	 * Returns the current action (sign up or log in) based on the value
	 * of the hidden action field
	 *
	 * @return {String} The action. Either "sign-up" or "log-in"
	 */
	get currentAction() {
		return this.$actionHiddenField.value == 'needs-to-register' ? 'sign-up' : 'log-in';
	}

	/**
	 * Returns the node of the hidden field that contains the ID of the
	 * current action
	 *
	 * @since 1.0
	 * @return {Node} Hidden action field
	 */
	get $actionHiddenField() {
		return document.querySelector( 'input[name="edd-purchase-var"]' );
	}

	/**
	 * Returns the node of the wrapper that changes when the user clicks
	 * on "Create account" or "Log in". EDD doesn't trigger a custom event,
	 * so we have to listen to the modifications made to this element.
	 *
	 * @since 1.0
	 * @return {Node} Username field wrapper
	 */
	get $dynamicContainer() {
		return document.getElementById( 'edd_checkout_login_register' );
	}
}

/**
 * Mirrors the content of the "Password" field to "Password Again",
 * so users don't have to enter the password twice
 *
 * @since 1.0
 */
class MirrorPasswordField {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Mirrors the value every time the gateway changes
		this.updateOnGatewayChanges();

		// Mirrors the value every time the action (sign-up/log-in) changes
		this.updateOnActionChange();
	}

	/**
	 * Waits for the payment method to load and adds an event
	 * listener to the password field to mirror the value
	 * to the hidden "Enter password again" field
	 *
	 * @since 1.0
	 * @return {undefined}
	 */
	mirrorValueInRealTime() {
		// Check if it has the required fields
		if ( this.hasFields ) {
			// Mirror the content of the "Password" field to "Password Again", so users don't have to enter the password twice
			this.$passwordField.addEventListener( 'input', () => {
				// Copy the password
				this.$passwordAgainField.value = this.enteredPassword;
			} );
		}
	}

	/**
	 * Waits for the payment method to load before mirroring the value
	 *
	 * @return {undefined}
	 */
	updateOnGatewayChanges() {
		// Wait for the payment method to load
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Mirror the value
			this.mirrorValueInRealTime();
		} );
	}

	/**
	 * Listens to the changes on the selected action (sign-up/log-in)
	 * using the custom event uo-edd-checkout/identification-source-change
	 *
	 * @return {undefined}
	 */
	updateOnActionChange() {
		document.body.addEventListener( 'uo-edd-checkout/identification-source-change', () => {
			// Mirror the value
			this.mirrorValueInRealTime();
		} );
	}

	/**
	 * Returns the value of the "Password" field
	 *
	 * @since 1.0
	 * @return {String} Password
	 */
	get enteredPassword() {
		return this.$passwordField.value;
	}

	/**
	 * Returns the node of the password field
	 *
	 * @since 1.0
	 * @return {Node} Password field
	 */
	get $passwordField() {
		return document.getElementById( 'edd_user_pass' );
	}

	/**
	 * Returns the node of the "Enter password again" field
	 *
	 * @since 1.0
	 * @return {Node} "Enter password again" field
	 */
	get $passwordAgainField() {
		return document.getElementById( 'edd_user_pass_confirm' );
	}

	/**
	 * Checks if both password fields are defined in the DOM
	 *
	 * @since 1.0
	 * @return {Boolean} true if both fields exist
	 */
	get hasFields() {
		return isDefined( this.$passwordField ) && isDefined( this.$passwordAgainField );
	}
}

/**
 * Mirrors the value of the "Email" field to "Username" field,
 * so users don't have to create a username
 *
 * @since 1.0
 */
class MirrorEmailField {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Mirrors the value every time the gateway changes
		this.updateOnGatewayChanges();

		// Mirrors the value every time the action (sign-up/log-in) changes
		this.updateOnActionChange();
	}

	/**
	 * Copies the value of the email field to the username field
	 *
	 * @since 1.0
	 * @return {undefined}
	 */
	mirrorValue() {
		// Copy the email address
		this.$usernameField.value = this.enteredEmail;
	}

	/**
	 * Mirrors the value of the "Email" field to the "Username" field
	 * in real time
	 *
	 * @since 1.0
	 * @return {undefined}
	 */
	mirrorValueInRealTime() {
		// Check if the required fields exists
		if ( this.hasFields ) {
			// Mirror on load
			this.mirrorValue();

			// Mirror the content of the "Email" field to "Username" field
			this.$emailField.addEventListener( 'input', () => {
				// Copy the email address
				this.mirrorValue();
			} );
		}
	}

	/**
	 * Waits for the payment method to load before mirroring the value
	 *
	 * @return {undefined}
	 */
	updateOnGatewayChanges() {
		// Wait for the payment method to load
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Mirror the value
			this.mirrorValueInRealTime();
		} );
	}

	/**
	 * Listens to the changes on the selected action (sign-up/log-in)
	 * using the custom event uo-edd-checkout/identification-source-change
	 *
	 * @return {undefined}
	 */
	updateOnActionChange() {
		document.body.addEventListener( 'uo-edd-checkout/identification-source-change', () => {
			// Mirror the value
			this.mirrorValueInRealTime();
		} );
	}

	/**
	 * Returns the value of the "Email" field
	 *
	 * @since 1.0
	 * @return {String} Email
	 */
	get enteredEmail() {
		return this.$emailField.value;
	}

	/**
	 * Returns the node of the "Email" field
	 *
	 * @since 1.0
	 * @return {Node} "Email" field
	 */
	get $emailField() {
		return document.getElementById( 'edd_email' );
	}

	/**
	 * Returns the node of the "Username" field
	 *
	 * @since 1.0
	 * @return {Node} "Username" field
	 */
	get $usernameField() {
		return document.getElementById( 'edd_user_login' );
	}

	/**
	 * Evaluates if the page has the required nodes to
	 * perform the actions
	 *
	 * @since 1.0
	 * @return {Boolean} True is the required fields exist
	 */
	get hasFields() {
		return isDefined( this.$emailField ) && isDefined( this.$usernameField );
	}
}

/**
 * Defines the visibility of the "Username" field. If the user
 * wants to sign up, then the field should be hidden, but if the
 * user wants to log in, then it should be visible.
 *
 * Log in:
 * - Email address: hidden
 * - Username: visible
 *
 * Sign up:
 * - Email address: visible
 * - Username: hidden
 */
class UsernameFieldVisibility {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Set visibility when the fields are ready
		this.setVisibilityOnLoad();
	}

	/**
	 * Listens to the payment method's AJAX call to load to evaluate
	 * the visibility of the fields
	 *
	 * @return {undefined}
	 */
	setVisibilityOnLoad() {
		// Wait for the payment method to load
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Check if the required fields exists
			if ( this.hasFields ) {
				// Check if the action (sign up/log in) changed
				this.setVisibilityOnActionChange();

				// Change visibility based on the current action
				this.setVisibilityBasedOnAction();
			}
		} );
	}

	/**
	 * Changes visibility based on the current action (sign up/log in)
	 *
	 * @return {undefined}
	 */
	setVisibilityBasedOnAction() {
		// Change visibility based on the current action
		if ( this.currentAction == 'sign-up' ) {
			// Sign up, so show "email", hide "username"
			this.setVisibility( {
				email: true,
				username: false,
			} );
		} else {
			// Log in, so show "username", hide "email"
			this.setVisibility( {
				email: false,
				username: true,
			} );
		}
	}

	/**
	 * Listens to the changes on the selected action (sign-up/log-in)
	 * using the custom event uo-edd-checkout/identification-source-change
	 *
	 * @return {undefined}
	 */
	setVisibilityOnActionChange() {
		document.body.addEventListener( 'uo-edd-checkout/identification-source-change', () => {
			// Change visibility based on the current action
			this.setVisibilityBasedOnAction();
		} );
	}

	/**
	 * Sets the current visibility of the username and email fields.
	 * This method makes the required DOM modifications to the elements.
	 *
	 * @param {Object} visibility Object with two properties, both boolean:
	 *                            email and username. The boolean determines
	 *                            the visibility of the field.
	 */
	setVisibility( visibility = {} ) {
		if ( isDefined( this.$emailFieldWrapper ) ) {
			// Check if we have to hide the email field
			if ( visibility.email ) {
				this.$emailFieldWrapper.style.display = '';
			} else {
				this.$emailFieldWrapper.style.display = 'none';
			}
		}

		if ( isDefined( this.$usernameFieldWrapper ) ) {
			// Check if we have to hide the username field
			if ( visibility.username ) {
				this.$usernameFieldWrapper.style.display = '';
			} else {
				this.$usernameFieldWrapper.style.display = 'none';
			}
		}
	}

	/**
	 * Returns the node of the email field
	 *
	 * @since 1.0
	 * @return {Node} Email field
	 */
	get $emailField() {
		return document.getElementById( 'edd_email' );
	}

	/**
	 * Returns the node of the email field wrapper
	 *
	 * @since 1.0
	 * @return {Node} Email field wrapper
	 */
	get $emailFieldWrapper() {
		return document.getElementById( 'edd-email-wrap' );
	}

	/**
	 * Returns the node of the username field
	 *
	 * @since 1.0
	 * @return {Node} Username field
	 */
	get $usernameField() {
		return document.getElementById( 'edd_user_login' );
	}

	/**
	 * Returns the node of the username field wrapper
	 *
	 * @since 1.0
	 * @return {Node} Username field wrapper
	 */
	get $usernameFieldWrapper() {
		return document.getElementById( 'edd-user-login-wrap' );
	}

	/**
	 * Returns the node of the hidden field that contains the ID of the
	 * current action
	 *
	 * @since 1.0
	 * @return {Node} Hidden action field
	 */
	get $actionHiddenField() {
		return document.querySelector( 'input[name="edd-purchase-var"]' );
	}

	/**
	 * Returns the current action (sign up or log in) based on the value
	 * of the hidden action field
	 *
	 * @return {String} The action. Either "sign-up" or "log-in"
	 */
	get currentAction() {
		return this.$actionHiddenField.value == 'needs-to-register' ? 'sign-up' : 'log-in';
	}

	/**
	 * Checks if the page has the required elements to perform the
	 * actions on the class
	 *
	 * @return {Boolean} True if the required elements exist
	 */
	get hasFields() {
		return (
			isDefined( this.$emailField ) && isDefined( this.$usernameField ) && isDefined( this.$actionHiddenField )
		);
	}
}

/**
 * Remove placeholders from all fields
 */
class RemovePlaceholders {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Wait for the payment method to load before removing the placeholder
		this.removePlaceholdersOnPaymentLoad();

		// Remove placeholders every time the action changes
		this.removePlaceholderOnActionChange();
	}

	/**
	 * Removes the placeholder of all the fields on the page.
	 *
	 * @return {undefined}
	 */
	removePlaceholders() {
		[ ...this.$fields ].forEach( ( $field ) => {
			// Remove placeholder
			$field.removeAttribute( 'placeholder' );
		} );
	}

	/**
	 * Waits for the payment method fields to load before removing
	 * the placeholder. EDD removes all the fields every time
	 * the payment method changes.
	 *
	 * @return {undefined}
	 */
	removePlaceholdersOnPaymentLoad() {
		// Remove them when the gateway loads
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Remove placeholders
			this.removePlaceholders();
		} );
	}

	/**
	 * Listens to the changes on the selected action (sign-up/log-in)
	 * using the custom event uo-edd-checkout/identification-source-change
	 *
	 * @return {undefined}
	 */
	removePlaceholderOnActionChange() {
		document.body.addEventListener( 'uo-edd-checkout/identification-source-change', () => {
			// Remove placeholders
			this.removePlaceholders();
		} );
	}

	/**
	 * Returns the node of all the fields (multiple nodes)
	 *
	 * @return {NodeList} The fields
	 */
	get $fields() {
		return document.querySelectorAll( '.edd-checkout input[type="text"], .edd-checkout input[type="password"]' );
	}
}

/**
 * Sets the correct tab index for the fields
 */
class TabIndex {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Define the correct order
		this.defineCorrectOrder();

		// Set the index tab
		this.setTabIndex();

		// Update tab index when the payment method changes
		this.updateOnGatewayChange();

		// Update every time the action changes (sign-up/log-in)
		this.updateOnActionChange();
	}

	/**
	 * Sets the desired order of the fields and saves it in a property
	 *
	 * @return {undefined}
	 */
	defineCorrectOrder() {
		this.fieldOrder = [
			'input[name="payment-mode"]',
			'.edd_checkout_register_login',
			'.edd_checkout_register_login',
			'#edd_user_login',
			'#edd_email',
			'#edd_user_pass',
			'#edd_first',
			'#edd_last',
			'#edd-user-login-submit input[type="submit"]',
			'#card_address',
			'#card_city',
			'#edd-card-zip-wrap input',
			'#edd-card-country-wrap select',
			'#edd-card-state-wrap select',
			'#card_name',
			'#edd-stripe-card-element iframe',
		];
	}

	/**
	 * Sets the correct tab-index of all fields on the page
	 *
	 * @return {undefined}
	 */
	setTabIndex() {
		setTimeout( () => {
			// Iterate selectors
			this.fieldOrder.forEach( ( fieldSelector, tabIndex ) => {
				// Get the field
				const $field = document.querySelector( fieldSelector );

				// Check if it's defined
				if ( isDefined( $field ) ) {
					// Change the tabindex
					$field.setAttribute( 'tabindex', tabIndex );
					$field.tabIndex = tabIndex;
				}
			} );
		}, 100 );
	}

	/**
	 * Listens to the changes on the selected action (sign-up/log-in)
	 * using the custom event uo-edd-checkout/identification-source-change
	 *
	 * @return {undefined}
	 */
	updateOnActionChange() {
		document.body.addEventListener( 'uo-edd-checkout/identification-source-change', () => {
			// Update tab index
			this.setTabIndex();
		} );
	}

	/**
	 * Listens to changes to the payment method and, once loaded,
	 * sets the tab-index again. EDD removes all the fields every
	 * time the payment method changes.
	 *
	 * @return {undefined}
	 */
	updateOnGatewayChange() {
		// Wait for the payment method to load
		// We can't use vanilla JS here, because EDD is using jQuery
		// to trigger this custom event
		jQuery( 'body' ).on( 'edd_gateway_loaded', () => {
			// Update the tab index
			this.setTabIndex();
		} );
	}
}

/**
 * Evaluates the validity of the fields everytime a field changes to decide whether it should enable the submit button
 */
class EnableButtonBasedOnValidity {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		// Check the validity of the main checkout form enable or disable the submit button
		this.checkValidity();
	}

	/**
	 * Checks the validity of the main checkout form everytime a field changes
	 */
	checkValidity() {
		/**
		 * The callback that will be invoked when the fields change.
		 * Checks the validity of the forms and changes the `disabled` attribute of the submit button
		 */
		const checkForm = throttle( () => {
			if ( this.$mainForm.checkValidity() ) {
				this.$submitButton.removeAttribute( 'disabled' );
			} else {
				this.$submitButton.setAttribute( 'disabled', 'disabled' );
			}
		}, 500 );

		/**
		 * Adds event listeners to all fields to listen changes in the form
		 */
		const listenAllFields = () => {
			// Check validity on load
			if ( ! this.$mainForm.checkValidity() ) {
				this.$submitButton.setAttribute( 'disabled', 'disabled' );
			}

			// Listen all fields
			this.$mainForm
				.querySelectorAll( 'input[type="checkbox"], input[type="radio"], select' ).forEach( ( $field ) => {
					// Remove the event listener, in case the field exists even after the gateway loaded
					$field.removeEventListener( 'change', checkForm );
					$field.addEventListener( 'change', checkForm );
				} );

			// Listen all fields
			this.$mainForm
				.querySelectorAll( 'input:not([type="checkbox"]):not([type="radio"])' ).forEach( ( $field ) => {
					// Remove the event listener, in case the field exists even after the gateway loaded
					$field.removeEventListener( 'input', checkForm );
					$field.addEventListener( 'input', checkForm );
				} );
		}

		jQuery( 'body' ).on( 'edd_gateway_loaded', listenAllFields );
		document.body.addEventListener( 'uo-edd-checkout/identification-source-change', listenAllFields );
	}

	/**
	 * Returns the main checkout form
	 * 
	 * @return {Node} Checkout form
	 */
	get $mainForm() {
		return document.getElementById( 'edd_purchase_form' );
	}

	/**
	 * Returns the submit button
	 * 
	 * @return {Node} Submit button
	 */
	get $submitButton() {
		return document.getElementById( 'edd-purchase-button' );
	}
}